/**
 * What's new: a release note that shows up once, when there is something new, and then gets out of the way.
 *
 *   <WhatsNewAnnouncer />                       in the dashboard layout: the card at the bottom left
 *   <WhatsNewModal entry={latest} isOpen onClose />   the full note (also opened from the Help menu)
 *
 * The card appears a moment after the page loads when the latest release is:
 *   - unseen in this browser (closing the card or the note marks it seen),
 *   - published in the last 14 days (old news never pops up for someone coming back after a month),
 *   - published after the account was made (a new account isn't greeted with news from before it existed).
 * Clicking it opens the full note in a modal you can scroll. The Help menu's "What's new" opens the same note any time,
 * and the profile menu links the full changelog.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useWhatsNew } from "@/hooks/useWhatsNew";
import { useUser } from "@/context/UserContext";
import { changelogPageUrl } from "@/services/changelog.service";
import Dialog from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import IconButton from "@/components/ui/IconButton";
import { formatDateTime } from "@/lib/format";
import posthog from "@/lib/posthog";
import type { ChangelogEntry } from "@/types/changelog";

const ANNOUNCE_FOR_DAYS = 14;
const SHOW_AFTER_MS = 1200;

const LABEL_STYLES: Record<string, string> = {
  New: "bg-primary-subtle text-primary-text",
  Improvement: "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300",
  Fix: "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300",
  Update: "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300",
};

const md = {
  a: (p: React.ComponentProps<"a">) => <a {...p} target="_blank" rel="noopener noreferrer" className="text-primary-text underline underline-offset-2" />,
  h1: (p: React.ComponentProps<"h2">) => <h3 className="mb-1.5 mt-5 text-base font-semibold text-fg" {...p} />,
  h2: (p: React.ComponentProps<"h2">) => <h3 className="mb-1.5 mt-5 text-base font-semibold text-fg" {...p} />,
  h3: (p: React.ComponentProps<"h3">) => <h4 className="mb-1 mt-4 text-sm font-semibold text-fg" {...p} />,
  p: (p: React.ComponentProps<"p">) => <p className="my-2 text-sm leading-relaxed text-fg-secondary" {...p} />,
  ul: (p: React.ComponentProps<"ul">) => <ul className="my-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-fg-secondary" {...p} />,
  ol: (p: React.ComponentProps<"ol">) => <ol className="my-2 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-fg-secondary" {...p} />,
  strong: (p: React.ComponentProps<"strong">) => <strong className="font-semibold text-fg" {...p} />,
  code: (p: React.ComponentProps<"code">) => <code className="rounded bg-surface-muted px-1 py-0.5 font-mono text-13 text-fg" {...p} />,
  pre: (p: React.ComponentProps<"pre">) => <pre className="my-3 overflow-x-auto rounded-lg bg-gray-950 p-3 text-xs leading-relaxed text-gray-100" {...p} />,
  blockquote: (p: React.ComponentProps<"blockquote">) => <blockquote className="my-2 border-l-2 border-line-strong pl-3 text-sm text-fg-muted" {...p} />,
  hr: () => <hr className="my-4 border-line" />,
  img: (p: React.ComponentProps<"img">) => (
    // eslint-disable-next-line @next/next/no-img-element
    <img {...p} alt={p.alt || ""} className="my-3 w-full rounded-lg border border-line" />
  ),
};

/** The first lines of a note as plain text, for the card. */
function excerpt(entry: ChangelogEntry): string {
  if (entry.description?.trim()) return entry.description.trim();
  return entry.body
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[#>*_`~|-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 240);
}

/** Should this release be announced to this person now? */
function isAnnounceable(entry: ChangelogEntry | null, accountCreatedAt: string | null | undefined, lastSeenAt: string | null): entry is ChangelogEntry {
  if (!entry?.date) return false;
  const published = new Date(entry.date).getTime();
  if (!Number.isFinite(published)) return false;
  if (Date.now() - published > ANNOUNCE_FOR_DAYS * 86_400_000) return false;
  if (accountCreatedAt && published < new Date(accountCreatedAt).getTime()) return false;
  if (lastSeenAt && published <= new Date(lastSeenAt).getTime()) return false;
  return true;
}

function Eyebrow({ entry }: { entry: ChangelogEntry }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="flex items-center gap-1.5 font-semibold uppercase tracking-wide text-primary-text">
        <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
        What&apos;s new
      </span>
      <span className={`rounded px-1.5 py-px font-medium ${LABEL_STYLES[entry.label] || LABEL_STYLES.Update}`}>{entry.label}</span>
      {entry.date && <span className="tabular-nums text-fg-muted">{formatDateTime(entry.date, "date")}</span>}
    </div>
  );
}

/** The full note: title, image and body, scrolling inside the modal, with a link to every release. */
export function WhatsNewModal({ entry, isOpen, onClose }: { entry: ChangelogEntry | null; isOpen: boolean; onClose: () => void }) {
  if (!entry) return null;
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={entry.title}
      width="2xl"
      footer={
        <>
          <Button variant="ghost" href={changelogPageUrl} external rightIcon="lucide:arrow-up-right" className="mr-auto">
            View full changelog
          </Button>
          <Button onClick={onClose}>Got it</Button>
        </>
      }
    >
      <div className="-mt-1">
        <Eyebrow entry={entry} />
        {entry.image && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={entry.image} alt="" className="mt-4 w-full rounded-xl border border-line" />
        )}
        <div className="mt-3">
          <Markdown remarkPlugins={[remarkGfm]} components={md}>
            {entry.body}
          </Markdown>
        </div>
      </div>
    </Dialog>
  );
}

/** The card at the bottom left: shown once per release, under the rules above. */
export function WhatsNewAnnouncer() {
  const { user } = useUser();
  const { latest, loaded, lastSeenAt, markAllSeen } = useWhatsNew();
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // One overlay at a time: not while the "how did you hear about us" question is waiting.
  const announce = useMemo(
    () => loaded && !!user && !user.acquisitionPromptPending && isAnnounceable(latest, user.createdAt, lastSeenAt),
    [loaded, user, latest, lastSeenAt]
  );

  useEffect(() => {
    if (!announce) return;
    const t = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [announce]);

  const dismiss = useCallback(() => {
    setVisible(false);
    markAllSeen();
    posthog.capture("whats_new_dismissed", { slug: latest?.slug });
  }, [markAllSeen, latest]);

  const open = () => {
    setVisible(false);
    setModalOpen(true);
    markAllSeen();
    posthog.capture("whats_new_opened", { slug: latest?.slug, from: "card" });
  };

  return (
    <>
      <AnimatePresence>
        {visible && latest && (
          <motion.aside
            aria-label="What's new"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-4 bottom-4 z-40 sm:inset-x-auto sm:left-4 sm:w-[340px]"
          >
            <div className="relative overflow-hidden rounded-card border border-line bg-raised shadow-modal">
              <div className="absolute right-2 top-2 z-10">
                <IconButton icon="lucide:x" label="Dismiss what's new" size="sm" onClick={dismiss} />
              </div>
              <button type="button" onClick={open} className="group block w-full cursor-pointer p-4 pr-10 text-left">
                <Eyebrow entry={latest} />
                <span className="mt-2 block text-sm font-semibold leading-snug text-fg group-hover:text-primary-text">{latest.title}</span>
                <span className="mt-1 line-clamp-3 block text-13 leading-5 text-fg-muted">{excerpt(latest)}</span>
                {latest.image && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={latest.image} alt="" className="mt-3 aspect-[16/8] w-full rounded-lg border border-line object-cover" />
                )}
                <span className="mt-3 inline-flex items-center gap-1 text-13 font-medium text-primary-text">
                  Read more
                  <Icon icon="lucide:arrow-right" className="text-sm transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
      <WhatsNewModal entry={latest} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
